import { useShareCardPermissions } from '@/hooks/share-card-permission.hook';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PAGE_TITLES } from '@/constants/titles';
import {
  CardIcon,
  FileIcon,
  UserGroup,
  AccountIcon,
  TransactionIcon,
  AuditIcon,
  EventIcon,
  CustomerIcon,
  CustomerRequestIcon,
  StablecoinManagementIcon,
  ReconciliationIcon
} from './icon';
import { Link } from 'react-router-dom';
import LokaIcon from '@/assets/brand/loka-icon.svg';
import LokaIconLight from '@/assets/brand/loka-icon-light.svg';
import LokaWordmark from '@/assets/brand/loka-logo.svg';
import LokaWordmarkLight from '@/assets/brand/loka-logo-light.svg';

const menuGroups = [
  {
    label: 'Infrastructure',
    items: [
      {
        title: 'Provider Accounts',
        url: '/providers',
        icon: FileIcon,
        pageTitle: PAGE_TITLES.PROVIDER,
      },
      {
        title: 'Virtual Accounts',
        url: '/account',
        icon: AccountIcon,
        pageTitle: PAGE_TITLES.ACCOUNT,
      },
      {
        title: 'Customer Transactions',
        url: '/transaction',
        icon: TransactionIcon,
        pageTitle: PAGE_TITLES.TRANSACTION,
      }
    ]
  },
  {
    label: 'Operations',
    items: [
      {
        title: 'Share Cards',
        url: '/share-cards',
        icon: CardIcon,
        pageTitle: PAGE_TITLES.SHARE_CARDS,
      },
      {
        title: 'Counterparts',
        url: '/counterparts',
        icon: ReconciliationIcon,
        pageTitle: PAGE_TITLES.COUNTERPART_MANAGEMENT,
      }
    ]
  },
  {
    label: 'Reports',
    items: [
      {
        title: 'Finance',
        url: '/finance',
        icon: AccountIcon,
        pageTitle: PAGE_TITLES.FINANCE,
      }
    ]
  },
  {
    label: 'Others',
    items: [
      {
        title: 'Card',
        url: '/card',
        icon: CardIcon,
        pageTitle: PAGE_TITLES.CARD,
      },
      {
        title: 'Customer',
        url: '/customer',
        icon: CustomerIcon,
        pageTitle: PAGE_TITLES.CUSTOMER,
      },
      {
        title: 'Customer Request',
        url: '/requests',
        icon: CustomerRequestIcon,
        pageTitle: PAGE_TITLES.CustomerRequest,
      },
      {
        title: 'User',
        url: '/user',
        icon: UserGroup,
        pageTitle: PAGE_TITLES.USER,
      },
      {
        title: 'Audit',
        url: '/audit',
        icon: AuditIcon,
        pageTitle: PAGE_TITLES.AUDIT,
      },
      {
        title: 'Event',
        url: '/event',
        icon: EventIcon,
        pageTitle: PAGE_TITLES.EVENT,
      },
      {
        title: 'Stablecoin Management',
        url: '/stablecoin-management',
        icon: StablecoinManagementIcon,
        pageTitle: PAGE_TITLES.STABLECOIN_MANAGEMENT,
      }
    ]
  }
];




export function AppSidebar() {
  const { state } = useSidebar();
  const { canView, canViewFunding } = useShareCardPermissions();
  const currentPageTitle = usePageTitle() as unknown as string | null;
  const isCollapsed = state === 'collapsed';
  const visibleGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.url === '/share-cards' ? canView : item.url === '/counterparts' ? canViewFunding : true,
      ),
    }))
    .filter((group) => group.items.length > 0);

  // Function to check if menu item is active
  const isActive = (pageTitle: string | null) => {
    if (!pageTitle) return false;
    return currentPageTitle === pageTitle;
  };

  return (
    <Sidebar
      collapsible="icon"
      className="bg-white dark:bg-[#272727] m-[0.5rem] sm:m-[1.5rem] rounded-[1.5rem] h-[96.5vh]"
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <div
              className={`logo relative flex justify-center ${isCollapsed ? 'mb-[1.5rem] pb-[1.5rem]' : 'mb-[1rem] pb-[1rem]'
                }`}
            >
              {isCollapsed ? (
                <>
                  <img className="w-[4rem] dark:hidden" src={LokaIcon} alt="Loka" />
                  <img className="w-[4rem] hidden dark:block" src={LokaIconLight} alt="Loka" />
                </>
              ) : (
                <>
                  <img className="w-[70%] dark:hidden" src={LokaWordmark} alt="Loka" />
                  <img className="w-[70%] hidden dark:block" src={LokaWordmarkLight} alt="Loka" />
                </>
              )}
              <div className=" bottom-0 absolute bg-[linear-gradient(90deg,rgba(224,225,226,0)_0%,rgba(224,225,226,1)_50%,rgba(224,225,226,0)_100%)] w-[100%] h-[0.1rem] left-1/2 -translate-x-1/2"></div>
            </div>
            <div className="flex flex-col gap-[2rem] mt-[1.8rem] overflow-y-scroll">
              {visibleGroups.map((group) => (
                <div key={group.label} className="flex flex-col gap-[0.5rem]">
                  {!isCollapsed && (
                    <div className="px-3 py-1.5 text-[1rem] uppercase tracking-wider text-muted-foreground font-semibold">
                      {group.label}
                    </div>
                  )}
                  <SidebarMenu className="flex flex-col gap-[1.2rem]">
                    {group.items.map((item, index) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          tooltip={isCollapsed && index > 0 ? item.title : undefined}
                          className={`text-[1.5rem] family-[montserrat] px-[1.2rem] rounded-[1.2rem] ${isActive(item.pageTitle)
                            ? 'bg-[#F6F6F6] dark:bg-[#1C1C1C] hover:bg-[#F6F6F6] dark:hover:bg-[#1C1C1C]'
                            : 'bg-transparent hover:bg-[#F0F0F0] dark:hover:bg-[#1C1C1C]'
                            }`}
                        >
                          {item.url === '#' ? (
                            <div
                              className={`flex h-[5rem] ${isCollapsed ? 'justify-center items-center' : 'gap-[1rem]'
                                } cursor-not-allowed opacity-50`}
                            >
                              <div
                                className={`icon ${isCollapsed ? 'w-[2.5rem] h-[2.5rem]' : 'w-[3rem] h-[3rem]'
                                  } rounded-[1rem] flex items-center justify-center flex-shrink-0 ${isActive(item.pageTitle)
                                    ? 'bg-white dark:bg-[#272727]'
                                    : 'bg-[#F0F0F0] dark:bg-[#1C1C1C]'
                                  }`}
                              >
                                <item.icon
                                  fill={isActive(item.pageTitle) ? '#3974F6' : 'currentColor'}
                                  className={`${isCollapsed ? 'h-[1.4rem] w-[1.4rem]' : 'h-[1.6rem] w-[1.6em]'
                                    }`}
                                />
                              </div>
                              <span
                                className={`${isCollapsed && index > 0 ? 'hidden' : ''} ${isCollapsed ? 'hidden' : 'flex items-center !whitespace-normal leading-tight !overflow-visible'
                                  }`}
                              >
                                {item.title}
                              </span>
                            </div>
                          ) : (
                            <Link
                              to={item.url}
                              className={`flex h-[5rem] ${isCollapsed ? 'justify-center items-center' : 'gap-[1rem]'
                                }`}
                            >
                              <div
                                className={`icon ${isCollapsed ? 'w-[2.5rem] h-[2.5rem]' : 'w-[3rem] h-[3rem]'
                                  } rounded-[1rem] flex items-center justify-center flex-shrink-0 ${isActive(item.pageTitle)
                                    ? 'bg-white dark:bg-[#272727]'
                                    : 'bg-[#F0F0F0] dark:bg-[#1C1C1C]'
                                  }`}
                              >
                                <item.icon
                                  size={isCollapsed ? 20 : 20}
                                  fill={isActive(item.pageTitle) ? '#3974F6' : 'currentColor'}
                                  className={`${isCollapsed ? 'h-[1.4rem] w-[1.4rem]' : 'h-[1.6rem] w-[1.6rem]'
                                    }`}
                                />
                              </div>
                              <span
                                className={`${isCollapsed && index > 0 ? 'hidden' : ''} ${isCollapsed ? 'hidden' : 'flex items-center !whitespace-normal leading-tight !overflow-visible'
                                  }`}
                              >
                                {item.title}
                              </span>
                            </Link>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </div>
              ))}
            </div>
            {/* <div
              className={`bottomHelp flex flex-col gap-[1.2rem] mt-auto   rounded-[1.2rem] ${isCollapsed
                ? 'items-center pb-[0.5rem]'
                : 'p-[1.2rem] bg-[#F0F0F0] dark:bg-[#1C1C1C]'
                }`}
            >
              <div
                className={`icon  ${isCollapsed ? '' : 'w-[5rem] h-[5rem]'
                  } flex items-center justify-center rounded-[1.2rem] ${isCollapsed ? ' ' : 'mb-[1.5rem] bg-white dark:bg-[#272727]'
                  }`}
              >
                <svg
                  className={` ${isCollapsed ? 'h-[2rem]' : 'h-[4rem]'} `}
                  viewBox="0 0 18 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.90179 2.375C5.40089 2.375 2.56055 5.39844 2.56055 9.125C2.56055 12.8516 5.40089 15.875 8.90179 15.875C12.4027 15.875 15.243 12.8516 15.243 9.125C15.243 5.39844 12.4027 2.375 8.90179 2.375ZM8.70362 13.0625C8.57298 13.0625 8.44527 13.0213 8.33664 12.944C8.22802 12.8667 8.14335 12.7569 8.09336 12.6284C8.04336 12.5 8.03028 12.3586 8.05577 12.2222C8.08126 12.0858 8.14417 11.9605 8.23655 11.8622C8.32893 11.7639 8.44662 11.6969 8.57476 11.6698C8.70289 11.6426 8.8357 11.6566 8.9564 11.7098C9.0771 11.763 9.18027 11.8531 9.25285 11.9687C9.32543 12.0844 9.36417 12.2203 9.36417 12.3594C9.36417 12.5459 9.29458 12.7247 9.1707 12.8566C9.04682 12.9884 8.87881 13.0625 8.70362 13.0625V13.0625ZM9.80806 9.47656C9.27268 9.85906 9.19903 10.2096 9.19903 10.5312C9.19903 10.6618 9.15032 10.787 9.0636 10.8793C8.97689 10.9716 8.85928 11.0234 8.73665 11.0234C8.61402 11.0234 8.49641 10.9716 8.4097 10.8793C8.32298 10.787 8.27427 10.6618 8.27427 10.5312C8.27427 9.76097 8.60718 9.14855 9.29217 8.65883C9.92894 8.2039 10.2889 7.91562 10.2889 7.28176C10.2889 6.85074 10.0577 6.52344 9.57918 6.28121C9.46655 6.22426 9.21588 6.16871 8.9074 6.17258C8.52032 6.17785 8.21977 6.27629 7.98825 6.47457C7.55163 6.84863 7.51464 7.25574 7.51464 7.26172C7.51171 7.32635 7.49685 7.38974 7.47091 7.44826C7.44497 7.50678 7.40845 7.5593 7.36345 7.6028C7.31844 7.6463 7.26583 7.67993 7.20861 7.70179C7.15139 7.72365 7.09069 7.73329 7.02996 7.73017C6.96924 7.72706 6.90969 7.71124 6.85472 7.68363C6.79974 7.65601 6.75041 7.61714 6.70954 7.56924C6.66868 7.52133 6.63707 7.46532 6.61654 7.40441C6.59601 7.34351 6.58695 7.27889 6.58988 7.21426C6.59351 7.12883 6.64933 6.35926 7.40796 5.70957C7.80132 5.37277 8.30168 5.19769 8.89419 5.18996C9.31364 5.18469 9.70765 5.26027 9.97484 5.39457C10.7744 5.79711 11.2137 6.46824 11.2137 7.28176C11.2137 8.47109 10.467 9.00512 9.80806 9.47656Z"
                    fill="#3974F6"
                  />
                </svg>
              </div>

              {!isCollapsed && (
                <>
                  <div className="content">
                    <div className="title mb-[0.5rem]">
                      <h3 className="font-bold text-[1.6rem]">Need help?</h3>
                    </div>

                    <div className="desc">
                      <p className="font-normal text-[1.4rem]">Please check our docs</p>
                    </div>
                  </div>

                  <Button className="w-full text-[1.4rem] rounded-[1.2rem] py-[2rem] bg-[#3974F6] text-white font-semibold">
                    DOCUMENTATION
                  </Button>
                </>
              )}
            </div> */}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
